import { escapeHtml } from '../ui.js';
import { tabsFor } from '../nav.js';

/**
 * A RAIL ITEM THAT HOLDS SEVERAL SCREENS (design_handoff_track_polish §1).
 *
 * Catalogue is Items · Services · Archive; Time is Timesheet · Approvals ·
 * Expenses. The screens underneath are untouched — each still renders itself
 * into a mount and knows nothing about the tabs. This draws the strip, keeps
 * the URL in step (`/catalogue/services`) and hands the panel to the right one.
 *
 * Role gating stays where it was: `tabsFor` filters by the same rules the rail
 * used, so Approvals is absent for anyone who could not see it before rather
 * than present-and-refused.
 *
 * Tabs per §11: 13.5px/500, 12px of bottom padding, a 2px --brand-blue underline
 * on the active one, and an orange dot where a tab holds something pending.
 */

/**
 * `self` is for a parent that is ALSO a screen. Time is the timesheet plus two
 * folded-in tabs, so it leads its own strip — and it is labelled "Timesheet"
 * there, not "Time", because inside Time that is what it is. Catalogue has no
 * screen of its own and passes no `self`.
 */
export function mountTabs(mount, {
  parent, tab, session, screens, defaultTab, self = null, pending = {}, onSelect, title = '',
}) {
  const roles = (session && session.roles) || [];
  const email = session && session.email;
  const available = (self ? [self] : []).concat(tabsFor(parent, roles, email));
  const ids = available.map((t) => t.id);
  const active = ids.includes(tab) ? tab : (ids.includes(defaultTab) ? defaultTab : ids[0]);

  // v4.0.0: the screen's own title sits ABOVE the strip (p24, p30–p32), so the
  // shell leaves a slot there; a screen fills it via tabHead(panel). Underline
  // tabs in navy (the shared .tabs/.tab style), not the old blue.
  // v4.0.0 (design export p10–p12): a parent that passes a `title` opens on its
  // page title, then the shared underline tabs (.tabs/.tab), with a pending
  // COUNT as a small orange chip ("Approvals 4"). Without a title the strip is
  // drawn as before, so Catalogue and Feedback are unchanged.
  if (title) {
    mount.innerHTML = `
      <div class="page-h tabshell-h"><h1>${escapeHtml(title)}</h1></div>
      <div class="tabs tabshell-tabs" role="tablist" aria-label="${escapeHtml(title)}">
        ${available.map((t) => {
          const on = t.id === active;
          const n = Number(pending[t.id]) || 0;
          return `<button type="button" role="tab" class="tab${on ? ' active' : ''}" aria-selected="${on}" data-tab="${escapeHtml(t.id)}">${escapeHtml(t.label)}${
            n > 0 ? `<span class="tab-count" aria-label="${n} waiting">${n}</span>` : ''}</button>`;
        }).join('')}
      </div>
      <div class="tabpanel"></div>`;
    const panelV4 = mount.querySelector('.tabpanel');
    mount.querySelectorAll('[data-tab]').forEach((b) => b.addEventListener('click', () => {
      if (b.dataset.tab === active) return;
      onSelect(b.dataset.tab);
    }));
    const renderV4 = screens[active];
    if (renderV4) renderV4(panelV4, session);
    return active;
  }

  mount.innerHTML = `
    <div class="tab-head"></div>
    <div class="tabstrip tabs" role="tablist" aria-label="${escapeHtml(parent)}">
      ${available.map((t) => {
        const on = t.id === active;
        return `<button class="tab${on ? ' active' : ''}" role="tab" aria-selected="${on}" data-tab="${escapeHtml(t.id)}">
          ${escapeHtml(t.label)}${pending[t.id]
            ? '<span aria-hidden="true" style="display:inline-block;width:6px;height:6px;border-radius:50%;background:var(--brand-orange);margin-left:7px;vertical-align:2px;"></span>'
            : ''}
        </button>`;
      }).join('')}
    </div>
    <div class="tabpanel"></div>`;

  const panel = mount.querySelector('.tabpanel');
  mount.querySelectorAll('[data-tab]').forEach((b) => b.addEventListener('click', () => {
    if (b.dataset.tab === active) return;
    onSelect(b.dataset.tab);
  }));

  const render = screens[active];
  if (render) render(panel, session);
  return active;
}

/**
 * The slot above the tab strip, for the screen's page head (title, subtitle,
 * buttons). Null when the screen is mounted on its own, outside the tabs — the
 * screen then draws its head inside its own mount.
 */
export function tabHead(panel) {
  const shell = panel && panel.classList && panel.classList.contains('tabpanel') ? panel.parentElement : null;
  return shell ? shell.querySelector(':scope > .tab-head') : null;
}
